import { Component } from '@theme/component';

/**
 * Progressive enhancement for the Azame nested tabs block.
 * Without JavaScript, each tab remains available as a native details element.
 */
class AzameTabsComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLButtonElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {HTMLDetailsElement[]} */
  #items = [];

  connectedCallback() {
    super.connectedCallback();
    this.#enhance();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#enhance();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  #enhance() {
    const selectedPanelId = this.#panels.find((panel) => !panel.hidden)?.id;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.querySelector(':scope > .azame-tabs__navigation')?.remove();

    this.#items = Array.from(
      this.querySelectorAll(':scope > .azame-tabs__fallback > [data-azame-tab-item]')
    ).filter((item) => item instanceof HTMLDetailsElement);
    this.#tabs = [];
    this.#panels = [];

    if (!this.#items.length) return;

    const navigation = document.createElement('div');
    navigation.className = 'azame-tabs__navigation';
    navigation.setAttribute('role', 'tablist');
    navigation.setAttribute('aria-label', this.dataset.tablistLabel || 'Informations');

    for (const [index, item] of this.#items.entries()) {
      const label = item.querySelector(':scope > [data-azame-tab-label]');
      const panel = item.querySelector(':scope > [data-azame-tab-panel]');
      if (!(label instanceof HTMLElement) || !(panel instanceof HTMLElement)) continue;

      const tab = document.createElement('button');
      const tabId = `${panel.id}-tab`;

      tab.type = 'button';
      tab.className = 'azame-tabs__button';
      tab.id = tabId;
      tab.textContent = label.textContent?.trim() || `Onglet ${index + 1}`;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);

      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabId);
      panel.tabIndex = 0;
      item.open = true;

      this.#tabs.push(tab);
      this.#panels.push(panel);
      navigation.append(tab);
    }

    const fallback = this.querySelector(':scope > .azame-tabs__fallback');
    fallback?.before(navigation);
    this.dataset.enhanced = 'true';

    const defaultIndex = this.#items.findIndex((item) => item.hasAttribute('data-default-active'));
    const preservedIndex = selectedPanelId ? this.#panels.findIndex((panel) => panel.id === selectedPanelId) : -1;
    this.select(preservedIndex >= 0 ? preservedIndex : Math.max(defaultIndex, 0), { scroll: false });

    const { signal } = this.#abortController;
    for (const [index, tab] of this.#tabs.entries()) {
      tab.addEventListener('click', () => this.select(index), { signal });
      tab.addEventListener('keydown', (event) => this.#onKeydown(event, index), { signal });
    }

    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
  }

  /**
   * @param {number} index
   * @param {{ focus?: boolean, scroll?: boolean }} [options]
   */
  select(index, options = {}) {
    if (!this.#tabs.length) return;

    const selectedIndex = Math.min(Math.max(index, 0), this.#tabs.length - 1);

    for (const [itemIndex, tab] of this.#tabs.entries()) {
      const isSelected = itemIndex === selectedIndex;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;
      this.#panels[itemIndex].hidden = !isSelected;
      this.#items[itemIndex]?.toggleAttribute('data-active', isSelected);
    }

    const selectedTab = this.#tabs[selectedIndex];
    if (options.focus) selectedTab.focus();

    if (options.scroll !== false) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      selectedTab.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #onKeydown(event, index) {
    const offsets = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -index,
      End: this.#tabs.length - 1 - index,
    };
    const offset = offsets[event.key];
    if (offset === undefined) return;

    event.preventDefault();
    const nextIndex = (index + offset + this.#tabs.length) % this.#tabs.length;
    this.select(nextIndex, { focus: true });
  }

  #onBlockSelect = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const item = event.target.closest('[data-azame-tab-item]');
    if (!(item instanceof HTMLDetailsElement) || !this.contains(item)) return;

    const index = this.#items.indexOf(item);
    if (index >= 0) this.select(index);
  };
}

if (!customElements.get('azame-tabs-component')) {
  customElements.define('azame-tabs-component', AzameTabsComponent);
}
